import { createAsyncThunk } from '@reduxjs/toolkit';
import { IDynamicFormValue, toFormFields2 } from 'src/lib';
import { IFilterObj } from 'src/components/filters';
import { transformDynamicFormValueToFilterPayload } from 'src/components/filters/utils/filter-form-utils';
import {
  addComment,
  deleteComment,
  deleteCommentsAndOpportunitiesForAllProduct,
  deleteProductCommentsAndOpportunities,
  downloadOpportunitySummary,
  editComment,
  fetchAddSKUFormFields,
  fetchBulkInputsTableRows,
  fetchDrawerData,
  fetchGridData,
  fetchLocalFilters,
  fetchOpportunities,
  fetchOpportunityForBulkInputsTable,
  fetchOpportunityForSummary,
  fetchOpportunitySummaryDetails,
  fetchProductsWithComments,
  saveScenarioPlanner,
  submitBulkComment,
  submitBulkOpportunities,
  submitDrawerOpportunities
} from '../api/planner-api';
import { IAddCommentPayload } from '../types/drawer-view-type';
import { IOpportunityPayload } from '../types/opportunity.model';
import getLocalFiltersPayload from '../utils/utils';
import { IBulkCommentsPayload } from '../types/bulk-inputs-table-data.model';
import { IBulkCommentDrawerItem } from '../types/bulk-comment-drawer';
import { IPlannerState } from '../types/planner-state.model';
import { IFilterState } from '../../common';

interface IDrawerAsyncThunkArgs {
  id: number;
  toggle: string;
  sourceId: number;
  moduleId: number;
  scenarioId: number;
}

interface IAddCommentAsyncThunkArgs extends IAddCommentPayload {
  scenarioId: number;
}

interface IUpdateCommentAsyncThunkArgs {
  commentId: number;
  commentText: string;
}

function getGlobalFilterValuesFromPlanner(thunkAPI: {
  getState: () => unknown;
}) {
  const {
    globalFilterValue,
    globalFilterCombinations,
    globalFilterFormFields
  } = (thunkAPI.getState() as unknown as any).filters as IFilterState;
  return transformDynamicFormValueToFilterPayload({
    formValue: globalFilterValue,
    formFields: globalFilterFormFields,
    combinations: globalFilterCombinations
  });
}

function getLocalFilterValuesFromPlanner(thunkAPI: {
  getState: () => unknown;
}) {
  const { localFilterFormFields, localFilterValue } = (
    thunkAPI.getState() as unknown as any
  ).planner as IPlannerState;
  return getLocalFiltersPayload(localFilterValue, localFilterFormFields);
}

export const getGridData = createAsyncThunk(
  '/api/planner/getGridData',
  async (payload: {
    sourceId: number;
    moduleId: number;
    scenarioId: number;
    localFilters: IDynamicFormValue;
    filters: IFilterObj[];
  }) => {
    const response = await fetchGridData(
      payload.moduleId,
      payload.sourceId,
      payload.scenarioId,
      payload.filters,
      payload.localFilters
    );
    return response.data;
  }
);

export const getDrawerData = createAsyncThunk(
  '/api/planner/getDrawerData',
  async (payload: IDrawerAsyncThunkArgs, thunkAPI) => {
    const filters = getGlobalFilterValuesFromPlanner(thunkAPI);
    const response = await fetchDrawerData(
      {
        id: payload.id,
        toggle: payload.toggle.toLowerCase(),
        filter_inputs: filters
      },
      payload.sourceId,
      payload.moduleId,
      payload.scenarioId
    );
    return response.data;
  }
);

export const addNewComment = createAsyncThunk(
  'addNewComment',
  async (payload: IAddCommentAsyncThunkArgs, thunkAPI) => {
    const response = await addComment(
      {
        ppg_id: payload.params.ppg_id,
        sku_id: payload.params.sku_id,
        comment_text: payload.params.comment_text,
        filter_inputs: getGlobalFilterValuesFromPlanner(thunkAPI)
      },
      payload.scenarioId
    );
    return response.data;
  }
);

export const updateComment = createAsyncThunk(
  'updateComment',
  async (payload: IUpdateCommentAsyncThunkArgs) => {
    const response = await editComment(payload);
    return response.data;
  }
);

export const deleteCommentById = createAsyncThunk(
  'deleteCommentById',
  async (commentId: number) => {
    const response = await deleteComment(commentId);
    return response;
  }
);

export const submitOpportunitiesForProduct = createAsyncThunk(
  '/api/planner/submitOpportunitiesForProduct',
  async (
    arg: {
      payload: IOpportunityPayload[];
      sourceId: number;
      moduleId: number;
      scenarioId: number;
    },
    thunkAPI
  ) => {
    const { moduleId, payload, sourceId, scenarioId } = arg;
    const filters = getGlobalFilterValuesFromPlanner(thunkAPI);
    await submitDrawerOpportunities(
      sourceId,
      moduleId,
      scenarioId,
      payload,
      filters
    );
    return payload;
  }
);

export const resetDrawer = createAsyncThunk(
  '/api/planner/resetDrawer',
  async (
    arg: {
      sourceId: number;
      moduleId: number;
      scenarioId: number;
      ppgId: number;
      /**
       * Will be undefined when we want the entire PPG and its SKUs opportunities to be reset
       */
      skuId: number;
      resetAll: boolean;
      /**
       * Drawer toggle
       */
      toggle: string;
    },
    thunkAPI
  ) => {
    const { moduleId, ppgId, resetAll, scenarioId, skuId, sourceId, toggle } =
      arg;
    await deleteProductCommentsAndOpportunities(
      sourceId,
      moduleId,
      scenarioId,
      {
        ppg_id: ppgId,
        sku_id: skuId,
        reset_all: resetAll,
        toggle,
        filter_inputs: getGlobalFilterValuesFromPlanner(thunkAPI)
      }
    );
    return { resetAll };
  }
);

export const resetAllOpportunities = createAsyncThunk(
  '/api/planner/resetAll',
  async (
    arg: {
      sourceId: number;
      moduleId: number;
      scenarioId: number;
      prodDetails: { ppg_id: number; sku_id: number }[];
      resetAll: boolean;
      /**
       * View of grid
       */
      toggle: string;
    },
    thunkAPI
  ) => {
    const { moduleId, resetAll, scenarioId, prodDetails, sourceId, toggle } =
      arg;
    await deleteCommentsAndOpportunitiesForAllProduct(
      sourceId,
      moduleId,
      scenarioId,
      {
        prod_details: prodDetails,
        reset_all: resetAll,
        toggle,
        filter_inputs: getGlobalFilterValuesFromPlanner(thunkAPI)
      }
    );
    return { resetAll };
  }
);

export const getOpportunities = createAsyncThunk(
  '/api/planner/getOpportunities',
  async (sourceId: number) => {
    const response = await fetchOpportunities(sourceId);
    return response;
  }
);

export const submitOpportunitiesForProductsInBulk = createAsyncThunk(
  '/api/planner/submitOpportunitiesForProductsInBulk',
  async (
    arg: {
      payload: IOpportunityPayload[];
      sourceId: number;
      moduleId: number;
      scenarioId: number;
    },
    thunkAPI
  ) => {
    const { moduleId, payload, sourceId, scenarioId } = arg;
    const filters = getGlobalFilterValuesFromPlanner(thunkAPI);
    await submitBulkOpportunities(
      sourceId,
      moduleId,
      scenarioId,
      payload,
      filters
    );
    return payload;
  }
);

export const addBulkCommentsByOpportunity = createAsyncThunk(
  '/api/planner/addBulkCommentsByOpportunity',
  async (
    args: {
      scenarioId: number;
      comment: string;
      products: IBulkCommentsPayload['prod_details'];
    },
    thunkAPI
  ) => {
    const { scenarioId, comment, products } = args;
    const filters = getGlobalFilterValuesFromPlanner(thunkAPI);
    const response = await submitBulkComment(scenarioId, {
      comment_text: comment,
      filter_inputs: filters,
      prod_details: products
    });
    return response;
  }
);

export const getProductsWithComments = createAsyncThunk(
  '/api/planner/getProductsWithComments',
  async (
    payload: {
      sourceId: number;
      moduleId: number;
      scenarioId: number;
    },
    thunkAPI
  ) => {
    const filters = getGlobalFilterValuesFromPlanner(thunkAPI);
    const localFilters = getLocalFilterValuesFromPlanner(thunkAPI);
    const response = await fetchProductsWithComments(
      payload.moduleId,
      payload.sourceId,
      payload.scenarioId,
      filters,
      localFilters
    );
    return response.data.map<IBulkCommentDrawerItem>((item, parentIndex) => ({
      id: item.id,
      index: parentIndex,
      itemName: item.name,
      parentId: -1,
      imageUrl: item.img_url,
      comments: item.comments,
      children: item.children.map<IBulkCommentDrawerItem>(
        (childItem, childIndex) => ({
          id: childItem.id,
          parentId: childItem.parent_id,
          index: childIndex,
          itemName: childItem.name,
          comments: childItem.comments,
          children: []
        })
      )
    }));
  }
);

export const getOpportunitySummaryTableData = createAsyncThunk(
  '/api/planner/getOpportunitySummaryData',
  async (
    payload: {
      sourceId: number;
      moduleId: number;
      scenarioId: number;
    },
    thunkAPI
  ) => {
    const filters = getGlobalFilterValuesFromPlanner(thunkAPI);
    const localFilters = getLocalFilterValuesFromPlanner(thunkAPI);
    const response = await fetchOpportunitySummaryDetails(
      payload.moduleId,
      payload.sourceId,
      payload.scenarioId,
      filters,
      localFilters
    );
    return response.data;
  }
);

export const getAddSKUFormFields = createAsyncThunk(
  '/api/planner/getAddSKUFormFields',
  async (payload: {
    sourceId: number;
    moduleId: number;
    scenarioId: number;
    filters: IFilterObj[];
  }) => {
    const response = await fetchAddSKUFormFields(
      payload.sourceId,
      payload.scenarioId,
      payload.moduleId,
      payload.filters
    );
    return {
      fields: toFormFields2(response.fields),
      combinations: response.combinations
    };
  }
);

export const getLocalFiltersData = createAsyncThunk(
  'getLocalFiltersData',
  async (
    args: { sourceID: number; scenarioId: number; filters: IFilterObj[] },
    thunkAPI
  ) => {
    const response = await fetchLocalFilters(
      args.sourceID,
      args.scenarioId,
      args.filters
    );
    const fields = toFormFields2(response.localFilter);
    const values = fields.reduce<IDynamicFormValue>(
      (prev, curr) => ({ ...prev, [curr.name]: curr.default_value }),
      {}
    );
    return { values, fields };
  }
);

export const savePlanner = createAsyncThunk(
  '/api/planner/savePlanner',
  async (scenarioId: number, thunkAPI) => {
    const filters = getGlobalFilterValuesFromPlanner(thunkAPI);
    const localFilters = getLocalFilterValuesFromPlanner(thunkAPI);
    const response = await saveScenarioPlanner(
      scenarioId,
      filters,
      localFilters
    );
    return response;
  }
);

export const getSummaryTableOpportunity = createAsyncThunk(
  '/api/home/getSummaryTableOpportunity',
  async (sourceId: number) => {
    const response = await fetchOpportunityForSummary(sourceId);
    return response.opportunities;
  }
);

export const getBulkInputsTableOpportunity = createAsyncThunk(
  '/api/home/getBulkInputsTableOpportunity',
  async (sourceId: number) => {
    const response = await fetchOpportunityForBulkInputsTable(sourceId);
    return response.opportunities;
  }
);

export const downloadSummary = createAsyncThunk(
  '/api/planner/savePlanner',
  async (
    args: {
      scenarioId: number;
      moduleId: number;
      sourceId: number;
    },
    thunkAPI
  ) => {
    const filters = getGlobalFilterValuesFromPlanner(thunkAPI);
    const localFilters = getLocalFilterValuesFromPlanner(thunkAPI);
    const scenarioName =
      (thunkAPI.getState() as any)?.simulatorHome?.currentScenario?.name ||
      'Summary';
    const response = await downloadOpportunitySummary(
      args.sourceId,
      args.moduleId,
      args.scenarioId,
      scenarioName,
      filters,
      localFilters
    );
    return response;
  }
);

export const getBulkInputsTableRows = createAsyncThunk(
  '/api/planner/getBulkInputsTableRows',
  async (
    args: {
      scenarioId: number;
      moduleId: number;
      sourceId: number;
      toggleView: string;
    },
    thunkAPI
  ) => {
    const filters = getGlobalFilterValuesFromPlanner(thunkAPI);
    const localFilters = getLocalFilterValuesFromPlanner(thunkAPI);
    const response = await fetchBulkInputsTableRows(
      args.sourceId,
      args.moduleId,
      args.scenarioId,
      filters,
      localFilters,
      args.toggleView
    );
    return response.data;
  }
);
